import React from "react";
import { getColor, getInitials } from "../utils/Helpers";
import { useCommunicationLayout } from "./CommunicationLayout";
import { ScopeTagsGroup } from "./ScopeTags";
import SkeletonWrapper from "./SkeltonWrapper";
import { useNavigate } from "react-router-dom";

interface EntitiesListProps {
    loading: boolean;
    entityCreationForms: any[];
    createEntity: (organizationID: string, questionnaireTypeID: string) => void;
    entities: any[];
    selectedEntity: any;
    setSelectedEntity: (entity: any) => void;
}

const EntitiesList = ({ loading, entityCreationForms, createEntity, entities, selectedEntity, setSelectedEntity }: EntitiesListProps) => {
    // Use media query for responsive detection
    const [isSmall, setIsSmall] = React.useState(false);
    
    React.useEffect(() => {
        const checkSize = () => {
            setIsSmall(window.innerWidth < 768);
        };
        checkSize();
        window.addEventListener("resize", checkSize);
        return () => window.removeEventListener("resize", checkSize);
    }, []);

    const { showList, setShowList } = useCommunicationLayout();
    const navigate = useNavigate();
    const [filter, setFilter] = React.useState<string>("");
    const [filteredEntities, setFilteredEntities] = React.useState(entities);

    React.useEffect(() => {
        setFilteredEntities(entities.filter((e) => e.name.toLowerCase().includes(filter.toLowerCase())));
    }, [filter, entities]);

    return (
        <>
            <SkeletonWrapper loading={loading}>
                <div className="flex flex-col gap-2">
                    {/* Action Buttons */}
                    <div className="flex flex-col gap-2">
                        {entityCreationForms.map((form) => (
                            <button
                                key={form.id}
                                className="px-4 py-2 bg-brand text-white rounded-md hover:bg-brand-dark transition-colors font-normal"
                                onClick={() => createEntity(form.organizationID, form.id)}
                            >
                                Create {form.name}
                            </button>
                        ))}
                        <button
                            className="px-4 py-2 bg-background-front text-text rounded-md hover:bg-background-back border border-border transition-colors font-normal"
                            onClick={() => navigate(`/disposal`)}
                        >
                            Upload Disposal Certificate
                        </button>
                    </div>

                    {/* Search and List */}
                    <div className="flex flex-col gap-2">
                        <input
                            type="text"
                            placeholder="Search"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className="w-full px-3 py-2 rounded-md border border-border bg-background-front text-text placeholder:text-text-weak focus:outline-none focus:ring-2 focus:ring-brand"
                        />
                        
                        {filteredEntities.map((entity) => (
                            <div
                                key={entity.id}
                                className={`p-2 rounded-md cursor-pointer flex flex-row gap-1 items-center transition-colors ${
                                    entity.id === selectedEntity?.id
                                        ? "bg-brand text-white"
                                        : "hover:bg-background-back"
                                }`}
                                onClick={() => {
                                    if (isSmall) setShowList(false);
                                    setSelectedEntity(entity);
                                    navigate("/entities/" + entity.id);
                                }}
                            >
                                <div
                                    className="w-8 min-w-8 h-8 min-h-8 rounded-full flex items-center justify-center text-xs font-medium"
                                    style={{
                                        backgroundColor: getColor(entity.id).background,
                                        color: getColor(entity.id).text,
                                    }}
                                >
                                    {getInitials(entity.name)}
                                </div>
                                <span className={`text-sm ${entity.id === selectedEntity?.id ? "font-bold" : ""}`}>
                                    {entity.name}
                                </span>
                                <div className={entity.id === selectedEntity?.id ? "text-white" : ""}>
                                    <ScopeTagsGroup questionnaire={entity} />
                                </div>
                            </div>
                        ))}
                        
                        {filteredEntities.length === 0 && (
                            <p className="text-text-weak text-sm">No entities found</p>
                        )}
                        
                        {filter.length > 0 && (
                            <button
                                className="px-4 py-2 bg-background-front text-text rounded-md hover:bg-background-back border border-border transition-colors font-normal"
                                onClick={() => setFilter("")}
                            >
                                Clear Search
                            </button>
                        )}
                    </div>
                </div>
            </SkeletonWrapper>
        </>
    );
};

export default EntitiesList;
